import { API_CONFIG } from '../config';
import { rememberEvents } from './eventRef';

// The static offline badge printer page (public/tools) reads this to reach the API for event imports.
try { localStorage.setItem('apiBaseUrl', API_CONFIG.baseUrl); } catch (_) { /* storage unavailable */ }
import { isOnline, isOfflineAllowed } from '../hooks/useOnlineStatus';
import { scanOffline } from './offlineScan';
import { getScannerName } from './scannerName';
import { getRegistration as getCachedRegistration, patchRegistrationCheckins } from './offlineDb';
import { queryRegistrationsOffline } from './offlineRegistrations';
import { newOfflineId, registerOffline } from './offlineOnSpot';
import { generateQrBase64 } from './qrCode';

// A network-level failure (device offline / server unreachable), as opposed to an
// HTTP error response. Used to decide when to fall back to the offline cache.
function isNetworkError(err) {
  if (!err) return false;
  if (err.serverUnavailable) return true;
  if (err.name === 'AbortError' || err.name === 'TypeError') return true;
  return /Failed to fetch|NetworkError|Load failed|network/i.test(err.message || '');
}

// 502/503/504 come from the proxy when the backend is down; treat them like a dropped network.
const GATEWAY_STATUSES = [502, 503, 504];
function serverUnavailableError(status) {
  const err = new Error(`Server unavailable (${status})`);
  err.serverUnavailable = true;
  return err;
}

// Read-through localStorage cache for navigation + scan-critical GETs so the app
// (and the QR scan flow) remains usable offline. Stores the last successful
// response and serves it when offline or when the request fails with a network
// error. ponytail: localStorage is bounded (~5MB); these payloads are small
// (event lists/config), unlike registrations which use IndexedDB.
const RESPONSE_CACHE_PREFIX = 'apiCache:';
function writeResponseCache(key, value) {
  try { localStorage.setItem(RESPONSE_CACHE_PREFIX + key, JSON.stringify(value)); } catch (_) { /* full/unavailable */ }
}
function readResponseCache(key) {
  try {
    const raw = localStorage.getItem(RESPONSE_CACHE_PREFIX + key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch (_) {
    return undefined;
  }
}

async function cachedGet(cacheKey, fetcher) {
  if (!isOnline()) {
    const cached = readResponseCache(cacheKey);
    if (cached !== undefined) return cached;
    return fetcher(); // no cache yet; let it attempt (and surface the error)
  }
  try {
    const data = await fetcher();
    writeResponseCache(cacheKey, data);
    return data;
  } catch (err) {
    if (isNetworkError(err)) {
      const cached = readResponseCache(cacheKey);
      if (cached !== undefined) return cached;
    }
    throw err;
  }
}

// Token management utility
class TokenManager {
  static getToken() {
    return localStorage.getItem('authToken');
  }

  static setToken(token) {
    localStorage.setItem('authToken', token);
  }

  static removeToken() {
    localStorage.removeItem('authToken');
  }

  static isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      // Basic token validation - check if it's not expired
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      // If token is malformed, consider it invalid
      console.error('Invalid token format:', error);
      this.removeToken();
      return false;
    }
  }

  static getTokenPayload() {
    const token = this.getToken();
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch {
      return null;
    }
  }
}

const PUBLIC_AUTH_ENDPOINT = /\/api\/auth\/(login|register|forgot-password|reset-password|event-login\/[^/]+)$/;
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

// API utility functions - production ready, no fallback data
export class ApiService {
  static async request(endpoint, options = {}) {
    const url = `${API_CONFIG.baseUrl}${endpoint}`;
    const token = TokenManager.getToken();
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add authorization header if token exists (public auth endpoints never need it)
    if (token && !PUBLIC_AUTH_ENDPOINT.test(endpoint)) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);
      
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (GATEWAY_STATUSES.includes(response.status)) {
        throw serverUnavailableError(response.status);
      }

      // Handle authentication errors
      if (response.status === 401) {
        // For public-friendly flows (like scan route), we should NOT force a redirect.
        // Just clear the token (if any) and surface the error to the caller.
        TokenManager.removeToken();
        // A token that was sent but rejected was revoked or expired: let the app sign out.
        if (token && headers.Authorization && typeof window !== 'undefined') {
          window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        }
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Some endpoints (e.g. void controllers) return an empty body; don't force JSON parsing.
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error.message);
      throw error;
    }
  }

  // Authentication API methods
  static async register(userData) {
    return this.request(API_CONFIG.endpoints.register, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Team sign-in for one event: refuses accounts without access to it.
  static async eventLogin(eventId, credentials) {
    return this.request(`/api/auth/event-login/${encodeURIComponent(eventId)}`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  static async getTeamPortal(eventId) {
    return this.request(`/api/public/team-portal/${encodeURIComponent(eventId)}`);
  }

  static async resolveDomain(host) {
    return this.request(`/api/public/domains/resolve?host=${encodeURIComponent(host)}`);
  }

  static async updateTeamPortal(eventId, portal) {
    return this.request(`/api/events/${eventId}/config/team-portal`, { method: 'PUT', body: JSON.stringify(portal) });
  }

  static async updateRoleUi(eventId, roleUi) {
    return this.request(`/api/events/${eventId}/config/role-ui`, { method: 'PUT', body: JSON.stringify(roleUi) });
  }

  static async updateEventSlug(eventId, slug) {
    return this.request(`/api/events/${eventId}/config/slug`, { method: 'PUT', body: JSON.stringify({ slug }) });
  }

  static async activateCustomDomain(eventId) {
    return this.request(`/api/events/${eventId}/config/custom-domain/activate`, { method: 'POST' });
  }

  static async getCustomDomainStatus(eventId) {
    return this.request(`/api/events/${eventId}/config/custom-domain/status`);
  }

  static async updateCustomDomain(eventId, customDomain) {
    return this.request(`/api/events/${eventId}/config/custom-domain`, {
      method: 'PUT',
      body: JSON.stringify({ customDomain }),
    });
  }

  static async setTeamMemberPassword(eventId, memberId, password) {
    return this.request(`/api/events/${eventId}/members/${memberId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  }

  static async getInviteAccessQuote(code, accessCode, formData) {
    return this.request(`/api/public/invites/${encodeURIComponent(code)}/access-quote`, {
      method: 'POST',
      body: JSON.stringify({ accessCode, formData }),
    });
  }

  static async login(credentials) {
    return this.request(API_CONFIG.endpoints.login, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  static async getCurrentUser() {
    return this.request(API_CONFIG.endpoints.getCurrentUser);
  }

  // Returns { token, user }: other devices are signed out, this session continues with the new token.
  static async changePassword(currentPassword, newPassword) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  static async logoutEverywhere() {
    return this.request('/api/auth/logout-all', { method: 'POST' });
  }

  static async forgotPassword(email) {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  static async resetPassword(token, newPassword) {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // Event API methods
  static async cloneEvent(eventId) {
    return this.request(`/api/events/${eventId}/clone`, { method: 'POST' });
  }

  static async createEvent(eventData) {
    return this.request(API_CONFIG.endpoints.createEvent, {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  static async getEvents(page = 0, size = 50) {
    const result = await cachedGet(`events:${page}:${size}`, () =>
      this.request(`${API_CONFIG.endpoints.getEvents}?page=${page}&size=${size}`));
    rememberEvents(result?.content || []);
    return result;
  }

  static async getRecentEvents(limit = 10) {
    const events = await this.request(`${API_CONFIG.endpoints.getEvents}/recent?limit=${limit}`);
    rememberEvents(events || []);
    return events;
  }

  static async getEvent(eventId) {
    const event = await cachedGet(`event:${eventId}`, () =>
      ApiService.request(API_CONFIG.endpoints.getEvent.replace(':eventId', eventId)));
    rememberEvents(event);
    return event;
  }

  static async getTicketing(ref, passId) {
    const query = passId ? `?pass=${encodeURIComponent(passId)}` : '';
    return cachedGet(`ticketing:${ref}:${passId || ''}`,
      () => ApiService.request(`/api/public/events/${encodeURIComponent(ref)}/ticketing${query}`));
  }

  static async sendEmailCode(eventId, email) {
    return ApiService.request(`/api/public/events/${eventId}/email-otp`, { method: 'POST', body: JSON.stringify({ email }) });
  }

  static async verifyEmailCode(eventId, email, code) {
    return ApiService.request(`/api/public/events/${eventId}/email-otp/verify`, { method: 'POST', body: JSON.stringify({ email, code }) });
  }

  static async checkPhoto(eventId, image) {
    return ApiService.request(`/api/public/events/${eventId}/photo-check`, { method: 'POST', body: JSON.stringify({ image }) });
  }

  static async createBooking(eventId, booking) {
    return ApiService.request(`/api/public/events/${eventId}/bookings`, { method: 'POST', body: JSON.stringify(booking) });
  }

  static async quoteBooking(eventId, quote) {
    return ApiService.request(`/api/public/events/${eventId}/bookings/quote`, { method: 'POST', body: JSON.stringify(quote) });
  }

  static async startBookingCheckout(eventId, bookingId) {
    return ApiService.request(`/api/public/events/${eventId}/bookings/${bookingId}/checkout`, { method: 'POST' });
  }

  static async checkBookingAccess(eventId, check) {
    return ApiService.request(`/api/public/events/${eventId}/access-check`, { method: 'POST', body: JSON.stringify(check) });
  }

  static async getBooking(eventId, bookingId, orderId) {
    const query = orderId ? `?order_id=${encodeURIComponent(orderId)}` : '';
    return ApiService.request(`/api/public/events/${eventId}/bookings/${bookingId}${query}`);
  }

  static async linkEventForm(eventId, schemaId) {
    return this.request(`/api/events/${eventId}/form`, { method: 'PUT', body: JSON.stringify({ schemaId }) });
  }

  static async getDocumentLimit() {
    return this.request('/api/public/limits');
  }

  static async saveDocumentLimit(maxDocumentMb) {
    return this.request('/api/admin/website/limits', { method: 'PUT', body: JSON.stringify({ maxDocumentMb }) });
  }

  static async getTicketingConfig(eventId) {
    return ApiService.request(`/api/events/${eventId}/config/ticketing`);
  }

  static async updateTicketingConfig(eventId, config) {
    return ApiService.request(`/api/events/${eventId}/config/ticketing`, { method: 'PUT', body: JSON.stringify(config) });
  }

  static async resolveEvent(ref) {
    return cachedGet(`eventRef:${ref}`, () => ApiService.request(`/api/public/events/resolve/${encodeURIComponent(ref)}`));
  }

  static async getPublicEvent(eventId) {
    // Public version without authentication for registration page
    return cachedGet(`publicEvent:${eventId}`, async () => {
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.getEvent.replace(':eventId', eventId)}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // If unauthorized, return null instead of redirecting
          return null;
        }
        if (GATEWAY_STATUSES.includes(response.status)) {
          throw serverUnavailableError(response.status);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    });
  }

  static async updateEvent(eventId, eventData) {
    return this.request(API_CONFIG.endpoints.updateEvent.replace(':eventId', eventId), {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });
  }

  static async deleteEvent(eventId) {
    return this.request(API_CONFIG.endpoints.deleteEvent.replace(':eventId', eventId), {
      method: 'DELETE',
    });
  }

  // Registration API methods
  static async registerUser(eventId, registrationData) {
    return ApiService.request(API_CONFIG.endpoints.registerUser.replace(':eventId', eventId), {
      method: 'POST',
      body: JSON.stringify(registrationData),
    });
  }

  // withAuth sends the login token when present; on-spot events restricted to logged-in staff need it.
  static async registerUserPublic(eventId, registrationData, { withAuth = false, throwOnUnauthorized = false } = {}) {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.registerUser.replace(':eventId', eventId)}`;
    const token = withAuth ? TokenManager.getToken() : null;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(registrationData),
    });

    if (GATEWAY_STATUSES.includes(response.status)) {
      throw serverUnavailableError(response.status);
    }
    if (!response.ok) {
      if (response.status === 401) {
        if (throwOnUnauthorized) {
          const err = new Error('Sign in again to upload registrations saved offline');
          err.authRequired = true;
          throw err;
        }
        // If unauthorized, return null instead of redirecting
        return null;
      }
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData?.message || errorData?.error || errorMessage;
      } catch (_) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (_) {}
      }
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }

    return await response.json();
  }

  /**
   * On-spot desk submit. With offline mode on, the registration carries a device-made ID and falls back to the
   * device queue when the server can't be reached (coupon holds and uploads need the server, so those never queue).
   */
  static async registerOnSpot(eventId, registrationData) {
    // Uploaded documents are already filed under the server-made userId, so those keep the normal path.
    const hasUploads = !!registrationData.userId && (registrationData.documentIds || []).length > 0;
    if (!isOfflineAllowed() || registrationData.couponHoldToken || hasUploads) {
      return ApiService.registerUserPublic(eventId, registrationData, { withAuth: true });
    }
    const payload = { ...registrationData, offlineId: registrationData.offlineId || newOfflineId(registrationData.formData) };
    if (!isOnline()) return registerOffline(eventId, payload);
    try {
      return await ApiService.registerUserPublic(eventId, payload, { withAuth: true });
    } catch (err) {
      if (isNetworkError(err)) return registerOffline(eventId, payload);
      throw err;
    }
  }

  static async applyCouponPublic(eventId, payload) {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.applyCoupon.replace(':eventId', eventId)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  static async removeCouponPublic(eventId, holdToken) {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.removeCoupon.replace(':eventId', eventId)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ holdToken }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  }

  static async listCoupons(page = 0, size = 50, eventId) {
    const params = new URLSearchParams({ page, size });
    if (eventId) params.append('eventId', eventId);
    return this.request(`${API_CONFIG.endpoints.listCoupons}?${params.toString()}`);
  }

  static async createCoupon(data) {
    return this.request(API_CONFIG.endpoints.createCoupon, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getCoupon(couponId) {
    return this.request(API_CONFIG.endpoints.getCoupon.replace(':couponId', couponId));
  }

  static async updateCoupon(couponId, data) {
    return this.request(API_CONFIG.endpoints.updateCoupon.replace(':couponId', couponId), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async activateCoupon(couponId) {
    return this.request(API_CONFIG.endpoints.activateCoupon.replace(':couponId', couponId), {
      method: 'POST',
    });
  }

  static async deactivateCoupon(couponId) {
    return this.request(API_CONFIG.endpoints.deactivateCoupon.replace(':couponId', couponId), {
      method: 'POST',
    });
  }

  static async getCouponRedemptions(couponId, page = 0, size = 50) {
    return this.request(
      `${API_CONFIG.endpoints.couponRedemptions.replace(':couponId', couponId)}?page=${page}&size=${size}`
    );
  }

  static async getRegistrations(eventId, page = 0, size = 50) {
    const response = await ApiService.request(`${API_CONFIG.endpoints.getRegistrations.replace(':eventId', eventId)}?page=${page}&size=${size}`);
    // Return full paginated response to preserve pagination metadata
    return response;
  }

  static async getRegistrationFilters(eventId) {
    return ApiService.request(`${API_CONFIG.endpoints.getRegistrations.replace(':eventId', eventId)}/filters`);
  }

  static async getRegistrationFacets(eventId) {
    return ApiService.request(`${API_CONFIG.endpoints.getRegistrations.replace(':eventId', eventId)}/facets`);
  }

  static async exportRegistrations(eventId, exportOptions = {}) {
    const queryParams = new URLSearchParams();
    
    if (exportOptions.filters) {
      Object.entries(exportOptions.filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
    }
    
    if (exportOptions.searchTerm) {
      queryParams.append('search', exportOptions.searchTerm);
    }
    
    if (exportOptions.visibleColumns) {
      queryParams.append('columns', exportOptions.visibleColumns.join(','));
    }

    const endpoint = `${API_CONFIG.endpoints.exportRegistrations.replace(':eventId', eventId)}?${queryParams.toString()}`;
    
    const response = await fetch(`${API_CONFIG.baseUrl}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return await response.text();
  }

  static async getRegistrationStats(eventId) {
    return ApiService.request(`${API_CONFIG.endpoints.getRegistrations.replace(':eventId', eventId)}/stats`);
  }

  // Payment Proof APIs
  static async getPaymentProofs(eventId, options = {}) {
    const queryParams = new URLSearchParams();
    
    if (options.page !== undefined) queryParams.append('page', options.page);
    if (options.size !== undefined) queryParams.append('size', options.size);
    if (options.search) queryParams.append('search', options.search);
    if (options.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
    }

    const endpoint = `${API_CONFIG.endpoints.getRegistrations.replace(':eventId', eventId)}/payment-proofs?${queryParams.toString()}`;
    return ApiService.request(endpoint);
  }

  static async getPaymentProofFilters(eventId) {
    return ApiService.request(`${API_CONFIG.endpoints.getRegistrations.replace(':eventId', eventId)}/payment-proofs/filters`);
  }

  static async uploadPaymentProof(eventId, registrationId, paymentData) {
    const formData = new FormData();
    formData.append('transactionId', paymentData.transactionId);
    formData.append('paymentMethod', paymentData.paymentMethod);
    formData.append('amount', paymentData.amount);
    
    if (paymentData.paymentProof) {
      formData.append('paymentProof', paymentData.paymentProof);
    }
    
    if (paymentData.file) {
      formData.append('file', paymentData.file);
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/registrations/${registrationId}/payment-proof`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return await response.json();
  }

  // Public version for payment proof upload (no authentication required)
  static async uploadPaymentProofPublic(eventId, registrationId, paymentData) {
    const formData = new FormData();
    formData.append('transactionId', paymentData.transactionId);
    formData.append('paymentMethod', paymentData.paymentMethod);
    formData.append('amount', paymentData.amount);
    
    if (paymentData.paymentProof) {
      formData.append('paymentProof', paymentData.paymentProof);
    }
    
    if (paymentData.file) {
      formData.append('file', paymentData.file);
    }


    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/registrations/${registrationId}/payment-proof`, {
      method: 'POST',
      // Don't set Content-Type header for FormData, let browser set it with boundary
      body: formData,
    });



    if (!response.ok) {
      if (response.status === 401) {

        throw new Error('Payment proof upload requires authentication. Please contact the event organizer.');
      }
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return await response.json();
  }

  static async approvePaymentProof(eventId, registrationId, proofId, data) {
    return ApiService.request(
      `/api/events/${eventId}/registrations/${registrationId}/payment-proof/${proofId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  static async rejectPaymentProof(eventId, registrationId, proofId, data) {
    return ApiService.request(
      `/api/events/${eventId}/registrations/${registrationId}/payment-proof/${proofId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  static async validateBulkUpload(eventId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/registrations/bulk/validate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Validation failed: ${response.statusText}`);
    }

    return await response.json();
  }

  static async getRegistrationFilters(eventId) {
    return ApiService.request(API_CONFIG.endpoints.getRegistrationFilters.replace(':eventId', eventId));
  }

  static async getFilteredRegistrations(eventId, filterRequest) {
    return ApiService.request(API_CONFIG.endpoints.getFilteredRegistrations.replace(':eventId', eventId), {
      method: 'POST',
      body: JSON.stringify(filterRequest),
    });
  }

  static async bulkUploadRegistrations(eventId, formData) {
    const token = TokenManager.getToken();
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/registrations/bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  static async updateRegistration(eventId, userId, registrationData) {
    // Backend expects UpdateRegistrationRequestDto with userId and formData
    const requestBody = {
      userId: userId,
      formData: registrationData
    };
    
    return this.request(API_CONFIG.endpoints.updateRegistration.replace(':eventId', eventId).replace(':userId', userId), {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    });
  }

  static async checkinUser(eventId, userId, checkinData) {
    return this.request(API_CONFIG.endpoints.checkinUser.replace(':eventId', eventId).replace(':userId', userId), {
      method: 'POST',
      body: JSON.stringify(checkinData),
    });
  }

  // Same receipt the confirmation email attaches. paidAt: IST "YYYY-MM-DD HH:mm"; plain skips the GST invoice.
  static async downloadPaymentReceipt(eventId, userId, { amount, paidAt, plain } = {}) {
    const params = new URLSearchParams();
    if (amount) params.set('amount', amount);
    if (paidAt) params.set('paidAt', paidAt);
    if (plain) params.set('plain', 'true');
    const query = params.toString();
    const response = await fetch(
      `${API_CONFIG.baseUrl}/api/v2/events/${eventId}/registrations/${encodeURIComponent(userId)}/receipt${query ? `?${query}` : ''}`,
      { headers: { Authorization: `Bearer ${TokenManager.getToken()}` } }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to generate the receipt (HTTP ${response.status})`);
    }
    return response.blob();
  }

  // v2 export: honours the table's current filters and the event's saved column layout.
  // format: 'csv' | 'xlsx' | 'zip' (Excel + every uploaded document).
  static async exportRegistrationsV2(eventId, filterRequest = {}, format = 'csv') {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/v2/events/${eventId}/registrations/export?format=${format}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${TokenManager.getToken()}`,
      },
      body: JSON.stringify(filterRequest),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  }

  // Mints a one-time link so the browser downloads the export itself (native progress, no blob in memory).
  static async createExportTicket(eventId, filterRequest = {}, format = 'csv') {
    const ticket = await this.request(`/api/v2/events/${eventId}/registrations/export-ticket?format=${format}`, {
      method: 'POST',
      body: JSON.stringify(filterRequest),
    });
    return { ...ticket, url: `${API_CONFIG.baseUrl}${ticket.url}` };
  }

  static async getExportColumns(eventId) {
    return this.request(`/api/v2/events/${eventId}/export-columns`);
  }

  static async saveExportColumns(eventId, columns) {
    return this.request(`/api/v2/events/${eventId}/export-columns`, {
      method: 'PUT',
      body: JSON.stringify(columns),
    });
  }

  static async deleteRegistration(eventId, userId, reason = '') {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    return this.request(`/api/events/${eventId}/registrations/${encodeURIComponent(userId)}${query}`, {
      method: 'DELETE',
    });
  }

  static async bulkDeleteRegistrations(eventId, userIds, reason = '') {
    return this.request(`/api/events/${eventId}/registrations/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify({ userIds, reason }),
    });
  }

  static async saveTableColumns(eventId, columns) {
    return this.request(`/api/v2/events/${eventId}/table-columns`, {
      method: 'PUT',
      body: JSON.stringify(columns),
    });
  }

  static async uploadRegistrationDocument(eventId, userId, name, file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/registrations/${encodeURIComponent(userId)}/documents`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${TokenManager.getToken()}` },
      body: formData,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || `Upload failed: ${response.status}`);
    }
    return response.json();
  }

  static async getEventAnalytics(eventId, refresh = false) {
    return this.request(`/api/events/${eventId}/analytics${refresh ? '?refresh=true' : ''}`);
  }

  static async exportRegistrations(eventId) {
    const token = TokenManager.getToken();
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.exportRegistrations.replace(':eventId', eventId)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  static async getRegistrationFilters(eventId) {
    return ApiService.request(API_CONFIG.endpoints.getRegistrationFilters.replace(':eventId', eventId), {
      method: 'GET',
    });
  }

  static async getFilteredRegistrations(eventId, filterRequest) {
    if (!isOnline() && isOfflineAllowed()) {
      return queryRegistrationsOffline(eventId, filterRequest);
    }
    try {
      return await ApiService.request(API_CONFIG.endpoints.getFilteredRegistrations.replace(':eventId', eventId), {
        method: 'POST',
        body: JSON.stringify(filterRequest),
      });
    } catch (err) {
      if (isNetworkError(err) && isOfflineAllowed()) {
        return queryRegistrationsOffline(eventId, filterRequest);
      }
      throw err;
    }
  }

  static async printUserBadgeWithData(eventId, userId, registrationData,printBadge) {
    const token = TokenManager.getToken();
    const url = new URL(`${API_CONFIG.baseUrl}/api/events/${eventId}/badges/${userId}/print`);
    url.searchParams.set("badgePrint", String(printBadge));

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(registrationData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  static async uploadFile(file, eventId, documentType, userId = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);
    formData.append('documentType', documentType);
    if (userId) {
      formData.append('userId', userId);
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/api/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }

  // QR Scanner API methods

  // Raw network scan (original behavior). Used directly by the background sync
  // so that replaying a queued scan never re-enters the offline fallback.
  static async scanQRNetwork(eventId, { qrCode, purpose, qrData, liveImageBase64, scanMode, clientScanId, scannedAt, scannerName }) {
    return this.request(API_CONFIG.endpoints.scanQR.replace(':eventId', eventId), {
      method: 'POST',
      body: JSON.stringify({
        qrCode,
        purpose,
        qrData,
        liveImageBase64,
        scanMode: scanMode || (liveImageBase64 ? 'face' : qrCode ? 'qr' : 'manual'),
        clientScanId,
        scannedAt,
        scannerName: scannerName ?? (getScannerName() || undefined),
      }),
    });
  }

  static async createStatsLink(eventId, hours, label) {
    return this.request(`/api/events/${eventId}/stats-links`, { method: 'POST', body: JSON.stringify({ hours, label }) });
  }

  static async getStatsLinks(eventId) {
    return this.request(`/api/events/${eventId}/stats-links`);
  }

  static async revokeStatsLink(eventId, token) {
    return this.request(`/api/events/${eventId}/stats-links/${encodeURIComponent(token)}`, { method: 'DELETE' });
  }

  // Public, no auth: a viewer of a shared link must never be signed out by a 401 here.
  static async getSharedStats(token) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/stats/${encodeURIComponent(token)}`);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.message || 'This stats link is not available.');
    return body;
  }

  static async myRegistration(path, { method = 'GET', body } = {}) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/my-registration${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Something went wrong. Please try again.');
    return data;
  }

  static async getMyRegistration(token) {
    return this.myRegistration(`/${encodeURIComponent(token)}`);
  }

  static async previewMyRegistrationEdit(token, formData) {
    return this.myRegistration(`/${encodeURIComponent(token)}/preview`, { method: 'POST', body: { formData } });
  }

  static async editMyRegistration(token, formData, paymentData) {
    return this.myRegistration(`/${encodeURIComponent(token)}/edit`, { method: 'POST', body: { formData, paymentData } });
  }

  static async cancelMyRegistration(token, reason) {
    return this.myRegistration(`/${encodeURIComponent(token)}/cancel`, { method: 'POST', body: { reason } });
  }

  static async requestMyRegistrationOtp(eventId, email) {
    return this.myRegistration('/otp', { method: 'POST', body: { eventId, email } });
  }

  static async verifyMyRegistrationOtp(eventId, email, code) {
    return this.myRegistration('/otp/verify', { method: 'POST', body: { eventId, email, code } });
  }

  static async downloadMyReceipt(token) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/my-registration/${encodeURIComponent(token)}/receipt`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Could not get the receipt.');
    }
    return response.blob();
  }

  static async previewBulkEmail(eventId, payload) {
    return this.request(`/api/events/${eventId}/bulk-email/preview`, { method: 'POST', body: JSON.stringify(payload) });
  }

  static async sendBulkEmail(eventId, payload) {
    return this.request(`/api/events/${eventId}/bulk-email`, { method: 'POST', body: JSON.stringify(payload) });
  }

  static async getBulkEmailJobs(eventId) {
    return this.request(`/api/events/${eventId}/bulk-email/jobs`);
  }

  static async cancelBulkEmailJob(eventId, jobId) {
    return this.request(`/api/events/${eventId}/bulk-email/jobs/${jobId}/cancel`, { method: 'POST' });
  }

  static async authorizedFetch(path, options = {}) {
    const response = await fetch(`${API_CONFIG.baseUrl}${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${TokenManager.getToken()}`, ...(options.headers || {}) },
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : null;
    if (!response.ok) throw new Error(data?.message || `Request failed (HTTP ${response.status})`);
    return data;
  }

  static async createCertificateBatch(eventId, file, purpose) {
    const form = new FormData();
    form.append('file', file);
    const query = purpose ? `?purpose=${encodeURIComponent(purpose)}` : '';
    return this.authorizedFetch(`/api/events/${eventId}/certificate-batches${query}`, { method: 'POST', body: form });
  }

  static async getCertificateBatches(eventId) {
    return this.request(`/api/events/${eventId}/certificate-batches`);
  }

  static async getCertificateBatch(eventId, batchId) {
    return this.request(`/api/events/${eventId}/certificate-batches/${batchId}`);
  }

  static async deleteCertificateBatch(eventId, batchId) {
    return this.request(`/api/events/${eventId}/certificate-batches/${batchId}`, { method: 'DELETE' });
  }

  static async uploadCertificateBatchFile(eventId, batchId, index, blob) {
    const form = new FormData();
    form.append('file', blob, `${index + 1}.pdf`);
    return this.authorizedFetch(`/api/events/${eventId}/certificate-batches/${batchId}/recipients/${index}/file`,
      { method: 'PUT', body: form });
  }

  static async sendCertificateBatch(eventId, batchId, subject, body) {
    return this.request(`/api/events/${eventId}/certificate-batches/${batchId}/send`, {
      method: 'POST',
      body: JSON.stringify({ subject, body }),
    });
  }

  static async cancelCertificateBatch(eventId, batchId) {
    return this.request(`/api/events/${eventId}/certificate-batches/${batchId}/cancel`, { method: 'POST' });
  }

  static async getWebsiteContent() {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/website`);
    if (!response.ok) throw new Error('Could not load website content');
    return response.json();
  }

  static async getPublicWebsiteEvents() {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/website/events`);
    if (!response.ok) throw new Error('Could not load events');
    return response.json();
  }

  static async getWebsiteAlbums() {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/website/albums`);
    if (!response.ok) throw new Error('Could not load albums');
    return response.json();
  }

  static async getWebsiteGallery(album, page = 0) {
    const params = new URLSearchParams({ page: String(page) });
    if (album) params.set('album', album);
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/website/gallery?${params}`);
    if (!response.ok) throw new Error(response.status === 404 ? 'Album not found' : 'Could not load photos');
    return response.json();
  }

  static async getAdminAlbums() {
    return this.request('/api/admin/website/albums');
  }

  static async createAlbum(album) {
    return this.request('/api/admin/website/albums', { method: 'POST', body: JSON.stringify(album) });
  }

  static async updateAlbum(id, patch) {
    return this.request(`/api/admin/website/albums/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
  }

  static async deleteAlbum(id) {
    return this.request(`/api/admin/website/albums/${id}`, { method: 'DELETE' });
  }

  static galleryPhotoUrl(id) {
    return `${API_CONFIG.baseUrl}/api/public/website/gallery/${id}/photo`;
  }

  static async submitTestimonial(payload) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/website/testimonials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Could not send your testimonial. Please try again.');
    }
  }

  static async submitDemoEnquiry(payload) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/website/enquiries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Could not send your request. Please try again.');
    }
  }

  static async getAdminTestimonials() {
    return this.request('/api/admin/website/testimonials');
  }

  static async reviewTestimonial(id, status) {
    return this.request(`/api/admin/website/testimonials/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
  }

  static async deleteTestimonial(id) {
    return this.request(`/api/admin/website/testimonials/${id}`, { method: 'DELETE' });
  }

  static async getAdminGallery() {
    return this.request('/api/admin/website/gallery');
  }

  static async uploadGalleryPhoto(file, caption, { logo = false, link, albumId } = {}) {
    const form = new FormData();
    form.append('file', file);
    const params = new URLSearchParams();
    if (caption) params.set('caption', caption);
    if (logo) params.set('logo', 'true');
    if (link) params.set('link', link);
    if (albumId) params.set('albumId', albumId);
    const query = params.toString() ? `?${params}` : '';
    return this.authorizedFetch(`/api/admin/website/gallery/photos${query}`, { method: 'POST', body: form });
  }

  static async addGalleryVideo(url, caption, albumId) {
    return this.request('/api/admin/website/gallery/videos', { method: 'POST', body: JSON.stringify({ url, caption, albumId }) });
  }

  static async updateGalleryItem(id, patch) {
    return this.request(`/api/admin/website/gallery/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
  }

  static async deleteGalleryItem(id) {
    return this.request(`/api/admin/website/gallery/${id}`, { method: 'DELETE' });
  }

  static async getInviteLinks(eventId) {
    return this.request(`/api/events/${eventId}/invite-links`);
  }

  static async createInviteLink(eventId, data) {
    return this.request(`/api/events/${eventId}/invite-links`, { method: 'POST', body: JSON.stringify(data) });
  }

  static async updateInviteLink(eventId, id, data) {
    return this.request(`/api/events/${eventId}/invite-links/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  static async deleteInviteLink(eventId, id) {
    return this.request(`/api/events/${eventId}/invite-links/${id}`, { method: 'DELETE' });
  }

  static async downloadInviteRegistrations(eventId, id) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/invite-links/${id}/registrations.csv`, {
      headers: { Authorization: `Bearer ${TokenManager.getToken()}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Download failed (HTTP ${response.status})`);
    }
    return response.blob();
  }

  static async getPublicInvite(code) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/public/invites/${encodeURIComponent(code)}`);
    if (!response.ok) throw new Error('Could not check the invite link');
    return response.json();
  }

  /** On-spot group: shared form answers, one registration per name. */
  static async registerGroup(eventId, payload) {
    const token = TokenManager.getToken();
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/register/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Group registration failed');
    return data;
  }

  static async getRegistrationHistory(eventId, userId) {
    return this.request(`/api/v2/events/${eventId}/registrations/${encodeURIComponent(userId)}/history`);
  }

  static async approvePendingChange(eventId, userId) {
    return this.request(`/api/v2/events/${eventId}/registrations/${encodeURIComponent(userId)}/pending-change/approve`, { method: 'POST' });
  }

  static async rejectPendingChange(eventId, userId, reason) {
    return this.request(`/api/v2/events/${eventId}/registrations/${encodeURIComponent(userId)}/pending-change/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  static async getScansByScanner(eventId) {
    return this.request(`/api/events/${eventId}/analytics/scanners`);
  }

  // Offline-first scan: use the local cache when offline (or when the network
  // request fails), otherwise hit the backend and keep the local cache in sync.
  static async scanQR(eventId, args) {
    const { purpose, qrData, scanMode } = args || {};

    if (!isOnline() && isOfflineAllowed()) {
      return scanOffline(eventId, { qrData, purpose, scanMode });
    }

    try {
      const result = await this.scanQRNetwork(eventId, args);
      // Best-effort: reflect the successful checkin in the local cache so a later
      // offline scan of the same code is correctly detected as already used.
      if (result && !result.error && result.userId) {
        patchRegistrationCheckins(eventId, result.userId, {
          [purpose]: { used: true, usedAt: new Date().toISOString(), purpose },
        }).catch(() => {});
      }
      return result;
    } catch (err) {
      if (isNetworkError(err) && isOfflineAllowed()) {
        return scanOffline(eventId, { qrData, purpose, scanMode });
      }
      throw err; // real HTTP/business error (e.g. 409 already redeemed)
    }
  }

  // Payment API methods
  static async approvePayment(eventId, userId) {
    return this.request(API_CONFIG.endpoints.approvePayment.replace(':eventId', eventId).replace(':userId', userId), {
      method: 'POST',
    });
  }

  static async rejectPayment(eventId, userId) {
    return this.request(API_CONFIG.endpoints.rejectPayment.replace(':eventId', eventId).replace(':userId', userId), {
      method: 'POST',
    });
  }

  // Registration approval API methods
  static async approveRegistration(eventId, userId, approvalData = {}) {
    return this.request(API_CONFIG.endpoints.approveRegistration.replace(':eventId', eventId).replace(':userId', userId), {
      method: 'POST',
      body: JSON.stringify(approvalData),
    });
  }

  static async rejectRegistration(eventId, userId, rejectionData = {}) {
    return this.request(API_CONFIG.endpoints.rejectRegistration.replace(':eventId', eventId).replace(':userId', userId), {
      method: 'POST',
      body: JSON.stringify(rejectionData),
    });
  }

  static async cancelRegistration(eventId, userId, cancellationData = {}) {
    return this.request(API_CONFIG.endpoints.cancelRegistration.replace(':eventId', eventId).replace(':userId', userId), {
      method: 'POST',
      body: JSON.stringify(cancellationData),
    });
  }

  static async getRegistrationStatus(eventId, userId) {
    return this.request(API_CONFIG.endpoints.getRegistrationStatus.replace(':eventId', eventId).replace(':userId', userId));
  }

  static async sendRegistrationConfirmationEmails(eventId, userIds) {
    return this.request(API_CONFIG.endpoints.bulkSendReminderEmails.replace(':eventId', eventId), {
      method: 'POST',
      body: JSON.stringify(userIds),
    });
  }

  static async bulkApproveRegistrations(eventId, userIds, reason = '') {
    return this.request(API_CONFIG.endpoints.bulkApproveRegistrations.replace(':eventId', eventId), {
      method: 'POST',
      body: JSON.stringify(userIds),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static async bulkRejectRegistrations(eventId, userIds, reason = '') {
    return this.request(API_CONFIG.endpoints.bulkRejectRegistrations.replace(':eventId', eventId), {
      method: 'POST',
      body: JSON.stringify(userIds),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Team Member APIs
  static async getTeamMembers(eventId) {
    return this.request(`/api/events/${eventId}/members`);
  }

  static async inviteTeamMember(eventId, memberData) {
    return this.request(`/api/events/${eventId}/members`, {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
  }

  static async updateTeamMember(eventId, memberId, memberData) {
    return this.request(`/api/events/${eventId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(memberData),
    });
  }

  static async removeTeamMember(eventId, memberId) {
    return this.request(`/api/events/${eventId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  static async acceptInvitation(eventId, memberId, token) {
    return this.request(`/api/events/${eventId}/members/${memberId}/accept?token=${token}`, {
      method: 'POST',
    });
  }

  static async declineInvitation(eventId, memberId, token) {
    return this.request(`/api/events/${eventId}/members/${memberId}/decline?token=${token}`, {
      method: 'POST',
    });
  }

  // Badge APIs
  static async getBadgeTemplates(eventId) {
    return this.request(`/api/events/${eventId}/badges/templates`);
  }

  static async createBadgeTemplate(eventId, templateData) {
    return this.request(`/api/events/${eventId}/badges/templates`, {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  }

  static async updateBadgeTemplate(eventId, templateId, templateData) {
    return this.request(`/api/events/${eventId}/badges/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData),
    });
  }

  static async deleteBadgeTemplate(eventId, templateId) {
    return this.request(`/api/events/${eventId}/badges/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  // Backend badge preview API for configuration (uses dummy data)
  static async previewBadgeConfig(eventId, badgeConfigDto) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/badges/preview`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
      body: JSON.stringify(badgeConfigDto),
    });

    if (!response.ok) {
      throw new Error(`Preview failed: ${response.statusText}`);
    }

    // Return the PDF as a Blob
    return await response.blob();
  }

  // Backend badge print API for specific user (uses real user data)
  static async printUserBadge(eventId, userId, registrationData) {
    return this.printUserBadgeWithData(eventId, userId, registrationData, false);
  }

  // Get registration by ID (RegistrationController.getRegistration)
  static async getRegistration(eventId, registrationId) {
    const fromCache = async () => {
      const cached = await getCachedRegistration(eventId, registrationId);
      if (!cached) return null;
      // Generate the QR client-side if the cached record has none (the list
      // endpoint used for priming typically omits the server QR image).
      const qrCodeBase64 = cached.qrCodeBase64 || (await generateQrBase64(cached.userId));
      return {
        userId: cached.userId,
        rfId: cached.rfId,
        formData: cached.formData || {},
        checkins: cached.checkins || {},
        multipleCheckins: cached.multipleCheckins || {},
        qrCodeBase64,
      };
    };

    if (!isOnline() && isOfflineAllowed()) {
      const cached = await fromCache();
      if (cached) return cached;
    }
    try {
      return await this.request(`/api/events/${eventId}/registrations/${registrationId}`);
    } catch (err) {
      if (isNetworkError(err) && isOfflineAllowed()) {
        const cached = await fromCache();
        if (cached) return cached;
      }
      throw err;
    }
  }


  static async generateBadge(eventId, registrationId, templateId, badgePrint = false) {
    const params = new URLSearchParams();
    if (badgePrint) {
      params.append('badgePrint', 'true');
    }
    
    const url = params.toString() ? 
      `/api/events/${eventId}/badges/generate?${params.toString()}` : 
      `/api/events/${eventId}/badges/generate`;

    return this.request(url, {
      method: 'POST',
      body: JSON.stringify({
        registrationId,
        templateId
      }),
    });
  }

  // New method specifically for badge printing with latest data
  static async generateBadgeForPrint(eventId, registrationId, templateId = null) {
    return this.generateBadge(eventId, registrationId, templateId, true);
  }


  // Badge Config APIs
  static async getBadgeConfig(eventId) {
    return this.request(`/api/events/${eventId}/badge-config`);
  }

  static async updateBadgeConfig(eventId, configData) {
    return this.request(`/api/events/${eventId}/badge-config`, {
      method: 'PUT',
      body: JSON.stringify(configData),
    });
  }

  static async patchBadgeConfig(eventId, configData) {
    return this.request(`/api/events/${eventId}/badge-config`, {
      method: 'PATCH',
      body: JSON.stringify(configData),
    });
  }

  // Print Configuration specific APIs
  static async getPrintConfig(eventId) {
    const config = await this.getBadgeConfig(eventId);
    return config?.printConfig || null;
  }

  static async updatePrintConfig(eventId, printConfig) {
    // Update only the printConfig portion of badge configuration
    return this.patchBadgeConfig(eventId, { printConfig });
  }

  // Enhanced generateBadge with print configurations
  static async generateBadgeWithPrintConfig(eventId, registrationId, printConfig = null) {
    const params = new URLSearchParams();
    params.append('badgePrint', 'true');
    if (printConfig) {
      params.append('printConfig', JSON.stringify(printConfig));
    }
    
    return this.request(`/api/events/${eventId}/badges/generate?${params.toString()}`, {
      method: 'POST',
      body: JSON.stringify({
        registrationId
      }),
    });
  }

  static async generateBadgePDF(eventId, userId) {
    return this.printUserBadgeWithData(eventId, userId, null, false);
  }

  static async uploadBadgeAsset(eventId, assetType, file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('assetType', assetType);
    
    return this.request(`/api/events/${eventId}/badge-config/upload-asset`, {
      method: 'POST',
      body: formData,
    });
  }

  static async uploadCsvData(eventId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.request(`/api/events/${eventId}/badge-config/upload-csv`, {
      method: 'POST',
      body: formData,
    });
  }

  static async generateCsvTemplate(eventId) {
    return this.request(`/api/events/${eventId}/badge-config/csv-template`);
  }

  // Form Schema APIs
  static async getFormSchemas() {
    return this.request(API_CONFIG.endpoints.getFormSchemas);
  }

  static async cloneFormSchema(schemaId) {
    return this.request(`/api/forms/schemas/${schemaId}/clone`, { method: 'POST' });
  }

  static async saveFormSchema(schemaData) {
    return this.request(API_CONFIG.endpoints.saveFormSchema, {
      method: 'POST',
      body: JSON.stringify(schemaData),
    });
  }

  static async updateFormSchema(schemaId, schemaData) {
    return this.request(API_CONFIG.endpoints.updateFormSchema.replace(':schemaId', schemaId), {
      method: 'PUT',
      body: JSON.stringify(schemaData),
    });
  }

  static async deleteFormSchema(schemaId) {
    return this.request(API_CONFIG.endpoints.deleteFormSchema.replace(':schemaId', schemaId), {
      method: 'DELETE',
    });
  }

  static async uploadFormSchemaAsset(assetType, file) {
    const formData = new FormData();
    formData.append('assetType', assetType);
    formData.append('file', file);
    const response = await fetch(`${API_CONFIG.baseUrl}/api/forms/schemas/upload-asset`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Upload failed: ${response.statusText}`);
    }
    return response.text();
  }

  // Payment Settings APIs
  static async getPaymentSettings() {
    return ApiService.request(API_CONFIG.endpoints.getPaymentSettings);
  }

  static async getGlobalPaymentSettings() {
    return ApiService.request(API_CONFIG.endpoints.getGlobalPaymentSettings);
  }

  static async getEventPaymentSettings(eventId) {
    return ApiService.request(`${API_CONFIG.endpoints.getEventPaymentSettings}?eventId=${eventId}`);
  }

  static async getPublicEventPaymentSettings(eventId) {
    // Only try event-specific payment settings (no global fallback)
    try {
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.getEventPaymentSettings}?eventId=${eventId}`;

      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });



      if (response.ok) {
        const data = await response.json();

        return data;
      }
    } catch (error) {
      console.log('getPublicEventPaymentSettings - Event-specific failed:', error.message);
    }


    return null;
  }

  static async getPublicEffectivePaymentSettings(eventId) {
    // Directly try the effective payment settings endpoint (best approach for public access)
    try {
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.getEffectivePaymentSettings}?eventId=${eventId}`;

      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });



      if (response.ok) {
        const data = await response.json();

        return data;
      }
    } catch (error) {
      console.log('getPublicEffectivePaymentSettings - Failed:', error.message);
    }


    return null;
  }

  static async getEffectivePaymentSettings(eventId) {
    return ApiService.request(`${API_CONFIG.endpoints.getEffectivePaymentSettings}?eventId=${eventId}`);
  }

  static async savePaymentSettings(settingsData) {
    return ApiService.request(API_CONFIG.endpoints.savePaymentSettings, {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  static async updatePaymentSettings(settingsData) {
    return ApiService.request(API_CONFIG.endpoints.updatePaymentSettings, {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  static async deletePaymentSettings(settingsId) {
    return ApiService.request(`${API_CONFIG.endpoints.deletePaymentSettings}?settingsId=${settingsId}`, {
      method: 'DELETE',
    });
  }

  // Event-specific payment settings CRUD
  static async createOrUpdateEventPaymentSettings(eventId, settingsData) {
    return ApiService.request(`${API_CONFIG.endpoints.savePaymentSettings}/event?eventId=${eventId}`, {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  // ——— Cashfree merchants (Easy Split vendors) ———
  static async getPaymentPlatformStatus() {
    return ApiService.request(`${API_CONFIG.endpoints.paymentMerchants}/platform`);
  }

  static async getMerchantBusinessTypes() {
    return ApiService.request(`${API_CONFIG.endpoints.paymentMerchants}/business-types`);
  }

  static async listPaymentMerchants() {
    return ApiService.request(API_CONFIG.endpoints.paymentMerchants);
  }

  static async createPaymentMerchant(data) {
    return ApiService.request(API_CONFIG.endpoints.paymentMerchants, { method: 'POST', body: JSON.stringify(data) });
  }

  static async updatePaymentMerchant(id, data) {
    return ApiService.request(`${API_CONFIG.endpoints.paymentMerchants}/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  static async deletePaymentMerchant(id) {
    return ApiService.request(`${API_CONFIG.endpoints.paymentMerchants}/${id}`, { method: 'DELETE' });
  }

  static async refreshPaymentMerchant(id) {
    return ApiService.request(`${API_CONFIG.endpoints.paymentMerchants}/${id}/refresh`, { method: 'POST' });
  }

  /** Creates an existing merchant on a gateway it isn't on yet ('CASHFREE' or 'PAYU'). */
  static async linkPaymentMerchant(id, provider, data) {
    return ApiService.request(`${API_CONFIG.endpoints.paymentMerchants}/${id}/link/${provider}`, { method: 'POST', body: JSON.stringify(data) });
  }

  // ——— Refunds ———
  static async getRegistrationRefunds(eventId, userId) {
    return ApiService.request(`/api/events/${eventId}/registrations/${encodeURIComponent(userId)}/refunds`);
  }

  static async createRegistrationRefund(eventId, userId, data) {
    return ApiService.request(`/api/events/${eventId}/registrations/${encodeURIComponent(userId)}/refunds`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async syncRefund(eventId, refundId) {
    return ApiService.request(`/api/events/${eventId}/refunds/${refundId}/sync`, { method: 'POST' });
  }

  /** Asks the gateway about this registration's open orders now and confirms it if one was paid. */
  static async recheckRegistrationPayment(eventId, userId) {
    return ApiService.request(`/api/events/${eventId}/registrations/${encodeURIComponent(userId)}/payment/recheck`, { method: 'POST' });
  }

  static async setMerchantPayuKey(id, childKey) {
    return ApiService.request(`${API_CONFIG.endpoints.paymentMerchants}/${id}/payu`, { method: 'PUT', body: JSON.stringify({ childKey }) });
  }

  // ——— Merchant payouts for "collect, transfer later" events ———
  static async getPaymentTransfers(eventId) {
    return ApiService.request(`/api/events/${eventId}/payment-transfers`);
  }

  static async runPaymentTransfers(eventId) {
    return ApiService.request(`/api/events/${eventId}/payment-transfers/run`, { method: 'POST' });
  }

  static async markPaymentTransferred(eventId, orderId, reference) {
    return ApiService.request(`/api/events/${eventId}/payment-transfers/${orderId}/manual`, {
      method: 'POST',
      body: JSON.stringify({ reference }),
    });
  }

  // ——— Event payment config (OFFLINE/ONLINE, merchant, platform fee) ———
  static async getEventPaymentConfig(eventId) {
    return ApiService.request(API_CONFIG.endpoints.eventPaymentConfig.replace(':eventId', eventId));
  }

  static async getPublicEventPaymentConfig(eventId) {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.eventPaymentConfig.replace(':eventId', eventId)}`;
    const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) return null;
    return response.json();
  }

  static async updateEventPaymentConfig(eventId, data) {
    return ApiService.request(API_CONFIG.endpoints.eventPaymentConfig.replace(':eventId', eventId), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Public create-order: creates the registration as PENDING_PAYMENT (or retries one via registrationId)
   * and returns the Cashfree payment session. The amount is always computed by the server.
   */
  static async createCashfreeOrderPublic(eventId, payload) {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.createCashfreeOrderPublic.replace(':eventId', eventId)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text || `Order create failed: ${response.status}`;
      try {
        const json = JSON.parse(text);
        message = json.message || json.error || message;
      } catch (_) {}
      throw new Error(message);
    }
    return response.json();
  }

  static async verifyCashfreeOrderPublic(eventId, orderId) {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.verifyCashfreeOrderPublic
      .replace(':eventId', eventId)
      .replace(':orderId', orderId)}`;
    const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Order verify failed: ${response.status}`);
    }
    return response.json();
  }

  static async deleteEventPaymentSettings(eventId) {
    return ApiService.request(`${API_CONFIG.endpoints.deletePaymentSettings}/event?eventId=${eventId}`, {
      method: 'DELETE',
    });
  }

  // Health check
  static async healthCheck() {
    return this.request(API_CONFIG.endpoints.health);
  }

  // Offline mode: compact registration rows for the device cache (works for public kiosks too).
  static async getOfflineSnapshot(eventId, { since, page = 0, size = 1000 } = {}) {
    const params = new URLSearchParams({ page, size });
    if (since) params.set('since', since);
    return this.request(`/api/v2/events/${eventId}/offline-snapshot?${params}`);
  }

  static async getSyncKeyStatus(eventId) {
    return this.request(`/api/v2/events/${eventId}/sync-key`);
  }

  static async rotateSyncKey(eventId) {
    return this.request(`/api/v2/events/${eventId}/sync-key`, { method: 'POST' });
  }

  static async getLanStatus() {
    return this.request('/api/v2/lan/status');
  }

  static async importLanEvent({ cloudUrl, eventId, syncKey }) {
    return this.request('/api/v2/lan/import', {
      method: 'POST',
      body: JSON.stringify({ cloudUrl, eventId, syncKey }),
    });
  }

  static async syncLanNow() {
    return this.request('/api/v2/lan/sync', { method: 'POST' });
  }

  static async unlinkLanEvent(eventId) {
    return this.request(`/api/v2/lan/links/${eventId}`, { method: 'DELETE' });
  }

  // QR Scan Purpose APIs
  static async getQRScanPurposes(eventId) {
    return cachedGet(`qrPurposes:${eventId}`, () =>
      this.request(`/api/events/${eventId}/qr-scan-purposes`));
  }

  static async createOrUpdateQRScanPurpose(eventId, config) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes`, {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  static async deleteQRScanPurpose(eventId, purpose) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes/${encodeURIComponent(purpose)}`, { method: 'DELETE' });
  }

  static async renameQRScanPurpose(eventId, purpose, newPurpose) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes/${encodeURIComponent(purpose)}/rename`, {
      method: 'POST',
      body: JSON.stringify({ purpose: newPurpose }),
    });
  }

  static async getQRScanPurposeStats(eventId, purpose) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes/${purpose}/stats`);
  }

  // Scan-page attendee search: full search when logged in, limited public search otherwise.
  static async searchScanAttendees(eventId, filterRequest) {
    if (TokenManager.isAuthenticated() || (!isOnline() && isOfflineAllowed())) {
      return this.getFilteredRegistrations(eventId, filterRequest);
    }
    return this.request(`/api/events/${eventId}/registrations/public-search`, {
      method: 'POST',
      body: JSON.stringify({ searchTerm: filterRequest.searchTerm }),
    });
  }

  static async syncQRScanPurposeStats(eventId, purpose) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes/${purpose}/stats/sync`, {
      method: 'POST',
    });
  }

  static async adjustQRScanPurposeStats(eventId, purpose, increment) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes/${purpose}/stats/adjust`, {
      method: 'POST',
      body: JSON.stringify({ increment }),
    });
  }

  static async refillQRScanInventory(eventId, purpose, refillData) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes/${purpose}/inventory/refill`, {
      method: 'POST',
      body: JSON.stringify(refillData),
    });
  }

  static async getQRScanInventoryHistory(eventId, purpose) {
    return this.request(`/api/events/${eventId}/qr-scan-purposes/${purpose}/inventory/history`);
  }

  // Event Email Configuration APIs
  static async getEventEmailConfig(eventId) {
    return this.request(`/api/events/${eventId}/emails`);
  }

  static async updateEventEmailConfig(eventId, emailConfig) {
    return this.request(`/api/events/${eventId}/emails`, {
      method: 'PUT',
      body: JSON.stringify(emailConfig),
    });
  }

  // Certificate APIs
  static async previewCertificate(eventId, certificateUrl = null) {
    const url = certificateUrl 
      ? `/api/events/${eventId}/certificates/preview?certificateUrl=${encodeURIComponent(certificateUrl)}`
      : `/api/events/${eventId}/certificates/preview`;
    
    const response = await fetch(`${API_CONFIG.baseUrl}${url}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Accept': '*/*',
      },
    });

    if (!response.ok) {
      throw new Error(`Preview failed: ${response.statusText}`);
    }

    return await response.blob();
  }

  static async generateCertificates(eventId, formData) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/certificates`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Generation failed: ${response.statusText}`);
    }

    return await response.text();
  }

  static async downloadCertificateUrls(eventId, certificateUrl = null) {
    const url = certificateUrl 
      ? `/api/events/${eventId}/certificates/download?certificateUrl=${encodeURIComponent(certificateUrl)}`
      : `/api/events/${eventId}/certificates/download?certificateUrl=null`;
    
    const response = await fetch(`${API_CONFIG.baseUrl}${url}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    return await response.json();
  }

  static async sendCertificatesToMail(eventId) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/certificates/send`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Send failed: ${response.statusText}`);
    }

    // Fire and forget - no response body expected
    return true;
  }

  // Team Member APIs
  static async inviteTeamMembers(eventId, teamMembers) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/members/bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ teamMembers }),
    });

    if (!response.ok) {
      throw new Error(`Invitation failed: ${response.statusText}`);
    }

    return await response.json();
  }

  static async getEventTeamMembers(eventId) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/members`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch team members: ${response.statusText}`);
    }

    return await response.json();
  }

  // Notification Template APIs (email / WhatsApp / SMS) - scoped per event
  static async getNotificationTemplates(eventId) {
    const response = await fetch(`${API_CONFIG.baseUrl}/v1/notifications/templates/${eventId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch templates: ${response.statusText}`);
    }

    return await response.json();
  }

  static async createNotificationTemplates(eventId, templates) {
    const response = await fetch(`${API_CONFIG.baseUrl}/v1/notifications/templates/${eventId}/bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(templates),
    });

    if (!response.ok) {
      throw new Error(`Failed to create templates: ${response.statusText}`);
    }

    return await response.json();
  }

  static async updateNotificationTemplate(eventId, externalId, template) {
    const response = await fetch(`${API_CONFIG.baseUrl}/v1/notifications/templates/${eventId}/${externalId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(template),
    });

    if (!response.ok) {
      throw new Error(`Failed to update template: ${response.statusText}`);
    }

    return await response.json();
  }

  // Badge Designer APIs
  static async getBadgeDesignerConfig(eventId, purpose = 'badge') {
    return cachedGet(`badgeConfig:${eventId}:${purpose}`, async () => {
      const response = await fetch(`${API_CONFIG.baseUrl}/api/v1/events/${eventId}/badge-config?purpose=${purpose}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${TokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No configuration exists yet
        }
        if (GATEWAY_STATUSES.includes(response.status)) {
          throw serverUnavailableError(response.status);
        }
        throw new Error(`Failed to fetch badge designer config: ${response.statusText}`);
      }

      return await response.json();
    });
  }

  static async saveBadgeDesignerConfig(eventId, purpose = 'badge', config) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/v1/events/${eventId}/badge-config?purpose=${purpose}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      throw new Error(`Failed to save badge designer config: ${response.statusText}`);
    }

    return await response.json();
  }

  // Get all existing badge configurations for an event
  static async getAllBadgeConfigs(eventId) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/v1/events/${eventId}/badge-configs`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return []; // No configurations exist yet
      }
      throw new Error(`Failed to fetch badge configurations: ${response.statusText}`);
    }

    return await response.json();
  }

  // Use existing scan QR API for badge printing
  static async scanQRForBadge(eventId, qrData, purpose = 'badge_printing') {
    const token = TokenManager.getToken();
    const headers = {
      'Content-Type': 'application/json',
    };

    // Only send Authorization header if token is present
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/events/${eventId}/scan`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ qrData, purpose }),
    });

    if (!response.ok) {
      throw new Error(`Failed to scan QR code: ${response.statusText}`);
    }

    return await response.json();
  }

  // Assign RFID to a registration
  static async assignRfid(eventId, { registrationId, rfId }) {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/events/${eventId}/assign-rfid`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TokenManager.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ registrationId, rfId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to assign RFID: ${response.statusText}`);
    }

    return await response.json();
  }

  // ---- Locker APIs ----

  static async configureLockers(eventId, { rangeFrom, rangeTo, numbers } = {}) {
    return this.request(API_CONFIG.endpoints.configureLockers.replace(':eventId', eventId), {
      method: 'POST',
      body: JSON.stringify({ rangeFrom, rangeTo, numbers }),
    });
  }

  static async importLockersCsv(eventId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.importLockersCsv.replace(':eventId', eventId)}`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${TokenManager.getToken()}` },
        body: formData,
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to import lockers: ${response.statusText}`);
    }
    return response.json();
  }

  static async getAvailableLockers(eventId, { limit = 15, all = false } = {}) {
    const query = all ? '?all=true' : `?limit=${limit}`;
    return this.request(API_CONFIG.endpoints.availableLockers.replace(':eventId', eventId) + query);
  }

  static async getLockerSummary(eventId) {
    return this.request(API_CONFIG.endpoints.lockerSummary.replace(':eventId', eventId));
  }

  static async assignLockers(eventId, userId, lockerNumbers) {
    return this.request(
      API_CONFIG.endpoints.assignLockers.replace(':eventId', eventId).replace(':userId', userId),
      { method: 'POST', body: JSON.stringify({ lockerNumbers }) }
    );
  }

  static async freeLockers(eventId, userId, lockerNumbers) {
    return this.request(
      API_CONFIG.endpoints.freeLockers.replace(':eventId', eventId).replace(':userId', userId),
      { method: 'POST', body: JSON.stringify({ lockerNumbers }) }
    );
  }

  static async getUserLockers(eventId, userId) {
    return this.request(
      API_CONFIG.endpoints.getUserLockers.replace(':eventId', eventId).replace(':userId', userId)
    );
  }

  static async getUserLockerHistory(eventId, userId) {
    return this.request(
      API_CONFIG.endpoints.getUserLockerHistory.replace(':eventId', eventId).replace(':userId', userId)
    );
  }

  static async getLockersDashboard(eventId, { page = 0, size = 20, search = '', status = '' } = {}) {
    const params = new URLSearchParams({ page, size });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    return this.request(
      `${API_CONFIG.endpoints.lockersDashboard.replace(':eventId', eventId)}?${params.toString()}`
    );
  }

  static async getLockerNumberHistory(eventId, number) {
    return this.request(
      API_CONFIG.endpoints.lockerNumberHistory
        .replace(':eventId', eventId)
        .replace(':number', encodeURIComponent(number))
    );
  }
}

export { TokenManager };
